import { Warning } from "@phosphor-icons/react"
import { ChevronDown, ChevronUp, X } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { formatRelative } from "./helpers"
import { StarRating } from "./star-rating"
import type { DashboardDraft } from "./types"

// Focus-mode conveyor: one draft at a time in a centered modal, with prev/next
// to step through the queue (chevrons + ↑/↓) and a position counter. Acting on
// a draft auto-advances to the next; deep-linkable from a notification email.
export function ReviewDraftModal({
  open,
  onOpenChange,
  draft,
  index,
  total,
  processing,
  onPrev,
  onNext,
  onApprove,
  onDismiss,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  draft: DashboardDraft | null
  index: number
  total: number
  processing: boolean
  onPrev: () => void
  onNext: () => void
  onApprove: (uid: string, text: string) => void
  onDismiss: (uid: string) => void
}) {
  const [text, setText] = useState(draft?.draft_excerpt ?? "")
  const [seededUid, setSeededUid] = useState(draft?.uid)

  // Re-seed the editor when a different draft comes into focus — adjusting state
  // during render (the React-recommended alternative to a setState effect).
  if (draft && draft.uid !== seededUid) {
    setSeededUid(draft.uid)
    setText(draft.draft_excerpt)
  }

  const canPrev = index > 0
  const canNext = index < total - 1

  // ↑/↓ step through the queue, except while editing the draft.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === "TEXTAREA" || tag === "INPUT") return
      if (e.key === "ArrowUp" && canPrev) {
        e.preventDefault()
        onPrev()
      } else if (e.key === "ArrowDown" && canNext) {
        e.preventDefault()
        onNext()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, canPrev, canNext, onPrev, onNext])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        // Top-aligned (not vertically centered) so the modal's top edge stays
        // put as content height changes between drafts — no jump on advance.
        // Two-column body keeps the response panel + actions in a fixed spot
        // regardless of review length, so the queue can be cleared quickly.
        // Fixed (not max) height so the modal never resizes between drafts.
        className="top-[8vh] flex h-[80vh] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
      >
        {/* Header: title · position · prev/next · close */}
        <div className="flex items-center justify-between border-b px-5 py-3">
          <DialogTitle className="text-sm font-medium">
            Review draft
          </DialogTitle>
          <div className="flex items-center gap-1">
            {total > 0 && (
              <span className="text-muted-foreground mr-1 text-xs tabular-nums">
                {index + 1} of {total}
              </span>
            )}
            <NavButton label="Previous" disabled={!canPrev} onClick={onPrev}>
              <ChevronUp className="size-4" />
            </NavButton>
            <NavButton label="Next" disabled={!canNext} onClick={onNext}>
              <ChevronDown className="size-4" />
            </NavButton>
            <DialogClose className="text-muted-foreground hover:text-foreground hover:bg-muted ml-1 flex size-7 items-center justify-center rounded-md transition-colors">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </div>

        {draft && (
          <>
            <div className="grid flex-1 grid-cols-2 divide-x overflow-hidden">
              {/* Left: the original review, in a card centered within a gray
                  well. The well scrolls on its own; a long review never pushes
                  the response panel or its actions out of place. */}
              <div className="bg-muted/40 overflow-y-auto p-5">
                <div className="bg-background w-full space-y-2 rounded-lg border p-4 shadow-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="text-sm font-semibold">
                        {draft.reviewer_name}
                      </p>
                      <StarRating rating={draft.rating} />
                    </div>
                    <p className="text-muted-foreground shrink-0 text-xs">
                      {formatRelative(draft.received_at ?? draft.created_at)}
                    </p>
                  </div>
                  {draft.flagged && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                      <Warning className="size-3" weight="fill" />
                      {draft.flag_reason ?? "Flagged"}
                    </span>
                  )}
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {draft.review_body ?? "Original review unavailable."}
                  </p>
                </div>
              </div>

              {/* Right: the editable draft, in a fixed white panel. The
                  textarea is height-confined with its own scroll so it stays
                  put near the top rather than stretching the full panel. */}
              <div className="bg-background flex flex-col p-5">
                <p className="mb-2 text-sm font-medium">Response</p>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={processing}
                  className="h-40 max-h-[60vh] min-h-32 resize-y overflow-y-auto"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-5 py-4">
              <Button
                variant="ghost"
                size="sm"
                disabled={processing}
                onClick={() => onDismiss(draft.uid)}
              >
                Dismiss
              </Button>
              <Button
                size="sm"
                className="rounded-sm"
                disabled={processing || text.trim().length === 0}
                onClick={() => onApprove(draft.uid, text)}
              >
                Post
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function NavButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex size-7 items-center justify-center rounded-md transition-colors",
        disabled
          ? "text-muted-foreground/40"
          : "text-muted-foreground hover:text-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  )
}
